import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../components/myTrack/AuthContext';
import { rootUrl } from '../../config/config';
import { FaPlay, FaHistory, FaDownload, FaTrash, FaCopy, FaDatabase, FaSyncAlt } from 'react-icons/fa';

const HISTORY_KEY = 'sql_runner_history';
const MAX_HISTORY = 30;

const SqlRunner = () => {
    const { token } = useAuth();
    const [sql, setSql] = useState('SELECT * FROM users LIMIT 10;');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [copied, setCopied] = useState(false);
    const [history, setHistory] = useState(() => {
        try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]'); }
        catch { return []; }
    });
    const textareaRef = useRef(null);

    const execute = useCallback(async (queryOverride) => {
        const query = (queryOverride || sql).trim();
        if (!query) return;

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await axios.post(
                `${rootUrl}/api/sql-runner/execute`,
                { sql: query },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                setResult(response.data.data);
                const entry = {
                    id: Date.now(),
                    sql: query,
                    timestamp: new Date().toISOString(),
                    rowCount: response.data.data.rowCount,
                    command: response.data.data.command
                };
                setHistory(prev => {
                    const updated = [entry, ...prev.filter(h => h.sql !== query)].slice(0, MAX_HISTORY);
                    localStorage.setItem(HISTORY_KEY, JSON.stringify(updated));
                    return updated;
                });
            } else {
                setError(response.data.message);
                setResult({ executionTime: response.data.executionTime });
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Request failed');
        } finally {
            setLoading(false);
        }
    }, [sql, token]);

    const handleKeyDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && (e.key === 'Enter' || e.key === 'r' || e.key === 'R')) {
            e.preventDefault();
            execute();
        }
        if (e.key === 'Tab') {
            e.preventDefault();
            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            const updated = sql.substring(0, start) + '  ' + sql.substring(end);
            setSql(updated);
            setTimeout(() => {
                if (textareaRef.current) {
                    textareaRef.current.selectionStart = start + 2;
                    textareaRef.current.selectionEnd = start + 2;
                }
            }, 0);
        }
    };

    const exportCsv = () => {
        if (!result?.rows?.length) return;
        const { columns, rows } = result;
        const csvLines = [
            columns.join(','),
            ...rows.map(row =>
                columns.map(col => {
                    const val = row[col];
                    if (val === null || val === undefined) return '';
                    const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
                    return str.includes(',') || str.includes('"') || str.includes('\n')
                        ? `"${str.replace(/"/g, '""')}"` : str;
                }).join(',')
            )
        ];
        const blob = new Blob([csvLines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sql_result_${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const copyJson = () => {
        if (!result?.rows?.length) return;
        navigator.clipboard.writeText(JSON.stringify(result.rows, null, 2)).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const clearHistory = () => {
        setHistory([]);
        localStorage.removeItem(HISTORY_KEY);
    };

    const formatTime = (iso) => new Date(iso).toLocaleString();

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            {/* Header */}
            <div className="p-6 lg:p-8">
                <div className="text-center mb-2">
                    <div className="flex items-center justify-center gap-3 mb-2">
                        <FaDatabase className="text-[#FF5733] text-3xl" />
                        <h1 className="text-4xl font-bold text-[#2a2e30]">SQL Runner</h1>
                    </div>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">
                        Execute SQL statements directly against the database
                    </p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">

                {/* Warning Banner */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
                    <span className="text-amber-500 text-xl mt-0.5">&#9888;</span>
                    <div>
                        <p className="text-amber-800 font-semibold text-sm">Admin Tool — Use with Caution</p>
                        <p className="text-amber-700 text-xs mt-0.5">
                            All queries are logged with your user identity. Destructive operations (DROP, DELETE, TRUNCATE, UPDATE)
                            will affect live data immediately. Use SELECT statements for safe assessment.
                        </p>
                    </div>
                </div>

                {/* Editor Panel */}
                <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                        <h2 className="text-lg font-semibold text-[#2a2e30]">Query Editor</h2>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setShowHistory(s => !s)}
                                className={`flex items-center gap-1.5 text-sm rounded-sm px-3 py-1.5 transition-colors ${showHistory ? 'bg-[#FF5733] text-white' : 'bg-slate-100 hover:bg-slate-200 text-slate-700'}`}
                            >
                                <FaHistory className="text-xs" />
                                History
                                {history.length > 0 && (
                                    <span className={`text-xs rounded-full px-1.5 py-0.5 ${showHistory ? 'bg-white/20' : 'bg-slate-200'}`}>
                                        {history.length}
                                    </span>
                                )}
                            </button>
                            <button
                                onClick={() => { setSql(''); setResult(null); setError(null); }}
                                className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-1.5"
                            >
                                <FaTrash className="text-xs" />
                                Clear
                            </button>
                            <button
                                onClick={() => execute()}
                                disabled={loading || !sql.trim()}
                                className="flex items-center gap-2 bg-[#FF5733] hover:bg-[#e64520] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-sm px-4 py-1.5 transition-colors"
                            >
                                {loading
                                    ? <FaSyncAlt className="text-xs animate-spin" />
                                    : <FaPlay className="text-xs" />
                                }
                                {loading ? 'Running...' : 'Run'}
                                <span className="text-xs opacity-60 hidden sm:inline">Ctrl+Enter</span>
                            </button>
                        </div>
                    </div>

                    {/* SQL Textarea */}
                    <textarea
                        ref={textareaRef}
                        value={sql}
                        onChange={e => setSql(e.target.value)}
                        onKeyDown={handleKeyDown}
                        className="w-full h-52 font-mono text-sm bg-gray-900 text-green-300 p-4 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-[#FF5733] leading-relaxed"
                        placeholder="Enter SQL query here...&#10;&#10;Examples:&#10;  SELECT * FROM users LIMIT 10;&#10;  SELECT table_name FROM information_schema.tables WHERE table_schema = 'public';"
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                    <p className="text-xs text-gray-400 mt-1.5">
                        <kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 text-gray-600">Ctrl+Enter</kbd> or <kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 text-gray-600">Ctrl+R</kbd> to run &bull; <kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 text-gray-600">Tab</kbd> inserts 2 spaces
                    </p>

                    {/* History Panel */}
                    {showHistory && (
                        <div className="mt-5 border-t pt-4">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold text-[#2a2e30]">Recent Queries</h3>
                                {history.length > 0 && (
                                    <button
                                        onClick={clearHistory}
                                        className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                                    >
                                        <FaTrash className="text-xs" /> Clear All
                                    </button>
                                )}
                            </div>
                            {history.length === 0 ? (
                                <p className="text-sm text-gray-400 py-3 text-center">No history yet. Run a query to see it here.</p>
                            ) : (
                                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                                    {history.map(entry => (
                                        <div
                                            key={entry.id}
                                            className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 group transition-colors"
                                            onClick={() => { setSql(entry.sql); setShowHistory(false); }}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="font-mono text-xs text-gray-700 line-clamp-2">{entry.sql}</p>
                                                <p className="text-xs text-gray-400 mt-1">
                                                    {formatTime(entry.timestamp)}
                                                    {entry.command && <span className="ml-2 bg-blue-50 text-blue-600 px-1.5 rounded">{entry.command}</span>}
                                                    {entry.rowCount !== undefined && <span className="ml-2">{entry.rowCount} rows</span>}
                                                </p>
                                            </div>
                                            <button
                                                onClick={e => { e.stopPropagation(); execute(entry.sql); setShowHistory(false); }}
                                                className="flex-shrink-0 text-xs bg-[#FF5733] text-white px-2.5 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                Run
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Results Panel */}
                {(result || error) && (
                    <div className="bg-white rounded-2xl shadow-xl p-6">

                        {/* Stats Bar */}
                        <div className="flex flex-wrap items-center gap-3 mb-4 pb-4 border-b">
                            {result?.command && (
                                <span className="text-xs font-mono bg-blue-50 text-blue-700 border border-blue-100 px-2.5 py-1 rounded-full">
                                    {result.command}
                                </span>
                            )}
                            {result?.rowCount !== undefined && (
                                <span className="text-sm text-gray-600">
                                    <strong className="text-[#2a2e30]">{result.rowCount}</strong> row{result.rowCount !== 1 ? 's' : ''}
                                </span>
                            )}
                            {result?.columns?.length > 0 && (
                                <span className="text-sm text-gray-600">
                                    <strong className="text-[#2a2e30]">{result.columns.length}</strong> column{result.columns.length !== 1 ? 's' : ''}
                                </span>
                            )}
                            {result?.executionTime !== undefined && (
                                <span className="text-sm text-gray-600">
                                    <strong className="text-[#2a2e30]">{result.executionTime}ms</strong> execution time
                                </span>
                            )}

                            {result?.rows?.length > 0 && (
                                <div className="ml-auto flex gap-2">
                                    <button
                                        onClick={copyJson}
                                        className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-sm px-3 py-1.5"
                                    >
                                        <FaCopy className="text-xs" />
                                        {copied ? 'Copied!' : 'Copy JSON'}
                                    </button>
                                    <button
                                        onClick={exportCsv}
                                        className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs rounded-sm px-3 py-1.5"
                                    >
                                        <FaDownload className="text-xs" />
                                        Export CSV
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Error Display */}
                        {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                                <p className="text-red-700 font-semibold text-sm mb-1">Query Error</p>
                                <pre className="text-red-600 text-xs font-mono whitespace-pre-wrap break-all">{error}</pre>
                            </div>
                        )}

                        {/* Results Table */}
                        {result?.rows?.length > 0 ? (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-max table-auto text-left">
                                    <thead>
                                        <tr className="bg-gray-50">
                                            <th className="p-3 border-b text-xs font-semibold text-gray-400 uppercase tracking-wider w-10 text-right">#</th>
                                            {result.columns.map(col => (
                                                <th key={col} className="p-3 border-b text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                                                    {col}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.rows.map((row, i) => (
                                            <tr key={i} className="hover:bg-gray-50 transition-colors">
                                                <td className="p-3 border-b text-xs text-gray-400 font-mono text-right">{i + 1}</td>
                                                {result.columns.map(col => (
                                                    <td key={col} className="p-3 border-b text-gray-700 font-mono text-xs">
                                                        {row[col] === null || row[col] === undefined ? (
                                                            <span className="text-gray-300 italic">NULL</span>
                                                        ) : typeof row[col] === 'object' ? (
                                                            <span className="text-blue-600">{JSON.stringify(row[col])}</span>
                                                        ) : typeof row[col] === 'boolean' ? (
                                                            <span className={row[col] ? 'text-green-600' : 'text-red-500'}>
                                                                {String(row[col])}
                                                            </span>
                                                        ) : (
                                                            <span
                                                                className="block max-w-xs truncate"
                                                                title={String(row[col])}
                                                            >
                                                                {String(row[col])}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : result?.rows?.length === 0 && !error ? (
                            <p className="text-center py-10 text-gray-400 text-sm">
                                Query executed successfully. No rows returned.
                            </p>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SqlRunner;
